import { useEffect, useId, useRef, useState } from "react";
import type { KeyboardEvent, ReactElement, RefObject } from "react";

import type { IconName } from "../../design-system/icons";
import { Icon } from "../atoms/Icon";
import { Kbd } from "../atoms/Kbd";
import { Popover } from "./Popover";
import type { FloatingAlign } from "./Popover";
import styles from "./Menu.module.css";

interface ActionEntry {
  readonly id: string;
  readonly label: string;
  readonly icon?: IconName | undefined;
  readonly disabled?: boolean | undefined;
  readonly onSelect: () => void;
}

/** One row of a menu: an action, a radio choice, a separator or a group label. */
export type MenuEntry =
  | (ActionEntry & {
      readonly kind: "item";
      readonly danger?: boolean | undefined;
      readonly shortcut?: string | undefined;
    })
  | (ActionEntry & { readonly kind: "radio"; readonly checked: boolean })
  | { readonly kind: "separator"; readonly id: string }
  | { readonly kind: "label"; readonly id: string; readonly label: string };

/** What the trigger must spread on the `<button>` it renders. */
export interface MenuTriggerProps {
  readonly ref: RefObject<HTMLButtonElement | null>;
  readonly "aria-haspopup": "menu";
  readonly "aria-expanded": boolean;
  readonly "aria-controls": string | undefined;
  readonly onClick: () => void;
  readonly onKeyDown: (event: KeyboardEvent<HTMLButtonElement>) => void;
}

export interface MenuProps {
  /** The accessible name of the menu (from `t()`). */
  readonly label: string;
  readonly entries: readonly MenuEntry[];
  readonly align?: FloatingAlign | undefined;
  readonly renderTrigger: (trigger: MenuTriggerProps) => ReactElement;
}

type Selectable = Extract<MenuEntry, { kind: "item" | "radio" }>;

const isEnabled = (entry: MenuEntry): entry is Selectable =>
  (entry.kind === "item" || entry.kind === "radio") && entry.disabled !== true;

/**
 * The WAI-ARIA menu button (the mockup's `role="menu"` popovers: org switcher, user menu, card menus).
 *
 * The trigger announces `aria-haspopup="menu"` and `aria-expanded`. Opening focuses the first enabled
 * item (ArrowUp on the trigger: the last). Inside, the arrows walk the enabled items with wrap and
 * Home/End jump; Enter or Space runs an item, closes and returns the focus to the trigger; Escape does
 * the same without running anything; Tab closes and lets the focus move on. Items are not tab stops.
 */
export function Menu({ label, entries, align = "start", renderTrigger }: MenuProps): ReactElement {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const itemRefs = useRef(new Map<string, HTMLButtonElement>());
  const menuId = useId();
  const enabled = entries.filter(isEnabled);

  const focusAt = (index: number): void => {
    const entry = enabled[(index + enabled.length) % enabled.length];
    if (entry !== undefined) itemRefs.current.get(entry.id)?.focus();
  };

  // The items mount with the popover, so the focus moves once they exist. This effect runs after the
  // popover's own initial focus (a child's effects run first), so it decides where the focus lands.
  const pendingFocus = useRef<number | null>(null);
  useEffect(() => {
    if (!open || pendingFocus.current === null) return;
    focusAt(pendingFocus.current);
    pendingFocus.current = null;
  });

  const openAt = (index: number): void => {
    pendingFocus.current = index;
    setOpen(true);
  };

  const close = (returnFocus: boolean): void => {
    setOpen(false);
    if (returnFocus) triggerRef.current?.focus();
  };

  const onTriggerKeyDown = (event: KeyboardEvent<HTMLButtonElement>): void => {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      openAt(event.key === "ArrowDown" ? 0 : enabled.length - 1);
    }
  };

  const onMenuKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    const current = enabled.findIndex((entry) => itemRefs.current.get(entry.id) === document.activeElement);
    const moves: Partial<Record<string, number>> = {
      ArrowDown: current < 0 ? 0 : current + 1,
      // With no item focused (the menu itself has it), ArrowUp starts from the end (verifier m5).
      ArrowUp: current < 0 ? enabled.length - 1 : current - 1,
      Home: 0,
      End: enabled.length - 1,
    };
    const target = moves[event.key];
    if (target !== undefined) {
      event.preventDefault();
      focusAt(target);
    } else if (event.key === "Tab") {
      close(false);
    }
  };

  const run = (entry: Selectable): void => {
    entry.onSelect();
    close(true);
  };

  return (
    <>
      {renderTrigger({
        ref: triggerRef,
        "aria-haspopup": "menu",
        "aria-expanded": open,
        "aria-controls": open ? menuId : undefined,
        onClick: () => {
          if (open) close(false);
          else openAt(0);
        },
        onKeyDown: onTriggerKeyDown,
      })}
      <Popover
        open={open}
        role="none"
        anchorRef={triggerRef}
        align={align}
        onClose={() => {
          setOpen(false);
        }}
      >
        <div id={menuId} role="menu" aria-label={label} tabIndex={-1} onKeyDown={onMenuKeyDown}>
          {entries.map((entry) => {
            if (entry.kind === "separator") {
              return <div key={entry.id} className={styles.separator} role="separator" />;
            }
            if (entry.kind === "label") {
              return (
                <div key={entry.id} className={styles.menuLabel} role="presentation">
                  {entry.label}
                </div>
              );
            }
            const danger = entry.kind === "item" && entry.danger === true;
            return (
              <button
                key={entry.id}
                ref={(element) => {
                  if (element === null) itemRefs.current.delete(entry.id);
                  else itemRefs.current.set(entry.id, element);
                }}
                type="button"
                role={entry.kind === "radio" ? "menuitemradio" : "menuitem"}
                aria-checked={entry.kind === "radio" ? entry.checked : undefined}
                aria-disabled={entry.disabled === true ? true : undefined}
                tabIndex={-1}
                className={[styles.item, danger ? styles.danger : undefined].filter(Boolean).join(" ")}
                onClick={() => {
                  if (entry.disabled !== true) run(entry);
                }}
              >
                {entry.icon === undefined ? null : <Icon name={entry.icon} />}
                <span className={styles.itemLabel}>{entry.label}</span>
                {entry.kind === "item" && entry.shortcut !== undefined ? <Kbd>{entry.shortcut}</Kbd> : null}
                {entry.kind === "radio" && entry.checked ? <Icon name="check" size="sm" /> : null}
              </button>
            );
          })}
        </div>
      </Popover>
    </>
  );
}
